import type {
  ReportingChartSpec,
  ReportingDataCell,
  ReportingDataset,
  ReportingDatasetColumn,
  ReportingDatasetRow,
} from 'src/agent/reporting/reportingPresentation.types'

import type {
  ReportingCurrencyDisplay,
  ReportingFieldReference,
  ReportingRenderableChartKind,
} from './reportingChart.types'

const MAX_SAFE_CHART_NUMBER = Number.MAX_SAFE_INTEGER
export const INVALID_VALUE = Symbol('invalid-reporting-value')
const VALID_CURRENCY_CODES = new Set([
  'AUD',
  'CAD',
  'CHF',
  'EUR',
  'GBP',
  'HKD',
  'JPY',
  'MXN',
  'NZD',
  'SGD',
  'USD',
])

export type InvalidValue = typeof INVALID_VALUE
type NumericValue = number | null | InvalidValue

export const supportedKinds = new Set<ReportingRenderableChartKind>([
  'kpi',
  'line',
  'horizontal_bar',
  'grouped_bar',
  'stacked_bar',
  'diverging_bar',
  'pie',
])

export const columnFor = (
  dataset: ReportingDataset,
  field: string,
): ReportingDatasetColumn | undefined =>
  dataset.columns.find((column) => column.key === field)

export const encodingFor = (
  chartSpec: ReportingChartSpec,
  channel: string,
): ReportingFieldReference | undefined => {
  const encoding = chartSpec.encoding
  if (!encoding || typeof encoding !== 'object') return undefined
  const reference = (encoding as Record<string, unknown>)[channel]
  if (!reference || typeof reference !== 'object') return undefined
  const field = (reference as Record<string, unknown>).field
  if (typeof field !== 'string' || field.length === 0) return undefined
  const format = (reference as Record<string, unknown>).format
  return {
    field,
    ...(typeof format === 'string' ? { format } : {}),
  }
}

const normalizeDecimal = (value: string): string | undefined => {
  const match = value.match(
    /^(-?)([0-9]+)(?:\.([0-9]+))?(?:[eE]([+-]?[0-9]+))?$/,
  )
  if (!match) return undefined
  const [, sign, whole, fraction = '', exponentText] = match
  const exponent = exponentText === undefined ? 0 : Number(exponentText)
  if (!Number.isSafeInteger(exponent) || Math.abs(exponent) > 1000)
    return undefined

  const digits = `${whole}${fraction}`
  const decimalIndex = whole.length + exponent
  let expanded: string
  if (decimalIndex <= 0) {
    expanded = `0.${'0'.repeat(-decimalIndex)}${digits}`
  } else if (decimalIndex >= digits.length) {
    expanded = `${digits}${'0'.repeat(decimalIndex - digits.length)}`
  } else {
    expanded = `${digits.slice(0, decimalIndex)}.${digits.slice(decimalIndex)}`
  }

  const [expandedWhole, expandedFraction = ''] = expanded.split('.')
  const normalizedWhole = expandedWhole.replace(/^0+(?=\d)/, '') || '0'
  const normalizedFraction = expandedFraction.replace(/0+$/, '')
  const normalized = `${normalizedWhole}${
    normalizedFraction ? `.${normalizedFraction}` : ''
  }`
  return normalized === '0' ? '0' : `${sign}${normalized}`
}

export const safeNumericValue = (
  value: ReportingDataCell | InvalidValue,
): NumericValue => {
  if (value === INVALID_VALUE) return INVALID_VALUE
  if (value === null) return null
  if (typeof value === 'number') {
    return Number.isFinite(value) && Math.abs(value) <= MAX_SAFE_CHART_NUMBER
      ? value
      : INVALID_VALUE
  }
  if (typeof value !== 'string' || value.length === 0) return INVALID_VALUE

  if (!/^-?(?:0|[1-9][0-9]*)(?:\.[0-9]+)?$/.test(value)) return INVALID_VALUE
  const normalized = normalizeDecimal(value)
  if (!normalized) return INVALID_VALUE
  const numeric = Number(value)
  if (
    !Number.isFinite(numeric) ||
    Math.abs(numeric) > MAX_SAFE_CHART_NUMBER ||
    normalizeDecimal(numeric.toString()) !== normalized
  ) {
    return INVALID_VALUE
  }
  return numeric
}

export const categoryValue = (
  value: ReportingDataCell | InvalidValue,
): string | number | boolean | null | InvalidValue => {
  if (value === INVALID_VALUE) return INVALID_VALUE
  if (value === null) return null
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value
  }
  return INVALID_VALUE
}

export const valueKey = (value: string | number | boolean | null): string =>
  `${typeof value}:${String(value)}`

export const rawValueFor = (
  row: ReportingDatasetRow,
  field: string,
): ReportingDataCell | InvalidValue =>
  Object.prototype.hasOwnProperty.call(row, field) ? row[field] : INVALID_VALUE

const minorUnitScaleFor = (currencyCode: string): number | InvalidValue => {
  try {
    const fractionDigits =
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
      }).resolvedOptions().maximumFractionDigits ?? 2
    return 10 ** fractionDigits
  } catch {
    return INVALID_VALUE
  }
}

export const currencyDisplayFor = (
  dataset: ReportingDataset,
  rows: ReportingDatasetRow[],
  valueField: string,
  format?: string,
): ReportingCurrencyDisplay | undefined | InvalidValue => {
  if (format !== 'currency') return undefined
  const valueColumn = columnFor(dataset, valueField)
  const currencyField = valueColumn?.currency_field
  const unit = valueColumn?.unit
  if (
    typeof currencyField !== 'string' ||
    (unit !== 'minor_currency' && unit !== 'major_currency') ||
    !columnFor(dataset, currencyField)
  ) {
    return INVALID_VALUE
  }

  let currencyCode: string | undefined
  for (const row of rows) {
    const value = rawValueFor(row, currencyField)
    if (
      typeof value !== 'string' ||
      !VALID_CURRENCY_CODES.has(value) ||
      (currencyCode !== undefined && currencyCode !== value)
    ) {
      return INVALID_VALUE
    }
    currencyCode = value
  }
  if (currencyCode === undefined) return INVALID_VALUE
  const minorUnitScale =
    unit === 'minor_currency' ? minorUnitScaleFor(currencyCode) : 1
  if (minorUnitScale === INVALID_VALUE) return INVALID_VALUE
  return { currencyCode, currencyField, minorUnitScale, unit }
}
